import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { faqs, howItWorks } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

import type { TeamConfig } from "../types";

const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/NBA%202025/MWC/Season_Share_1486x148_Banner.png";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NBA%202025/MWC/2026_Basketball_Championship_CU1_Light_Background_RGB.png";

const config: TeamConfig = {
  teamSchema: "mountainwest_flex",
  browserTitle: "Mountain West Conference Flex",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Mountain West Conference",

  packs: {
    enabled: true,
    dontOpenPackModal: false,
    list: [
      {
        id: 1,
        amount: 3,
        title: "3 Session Pack",
        description: "Select 3 sessions for 15% off your total ticket price!",
        offerMoreMessage:
          "Would you like to add 2 more sessions and get 20% off your tickets?",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Pick 3 Sessions",
            description: "",
            max: 3,
            min: 3,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 0,
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 3 games to continue!",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 2 games to continue!",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 1 game to continue!",
          },
        ],
      },
      {
        id: 2,
        amount: 5,
        title: "5 Session Pack",
        description: "Select 5 sessions for 20% off your total ticket price!",
        offerMoreMessage: "",
        buckets: [
          {
            id: 1,
            title: "Pick 5 Sessions",
            description: "",
            max: 5,
            min: 5,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 1,
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 5 games to continue!",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 4 games to continue!",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 3 games to continue!",
          },
          {
            selectedEventCount: 3,
            progressBarText: "Add 2 games to continue!",
          },
          {
            selectedEventCount: 4,
            progressBarText: "Add 1 game to continue!",
          },
        ],
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10460-basketball-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/mountainwest/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: FindGamesBanner,
    findGamesBannerImage: FindGamesBanner,
    checkoutStatusPageImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,
  },

  colors: {
    primary: "#4f2d7f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      progressBarAditionalText: "",
      progressBarSuccessText: "",
      chooseAddOnsTabTitle: "",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your Mountain West Conference purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
